import clsx from 'clsx';
import React, { Fragment, useState } from 'react';
import Button from '../../../atoms/button';
import CountrySelector from '../../../atoms/countySelector';
import { Link } from '../../../atoms/link/Link';

import app from '@frontastic/catwalk/js/app/app';

import { useScrollLock } from '@srcDS/utils/useLockScroll';
import { getHexColor } from '@srcDS/utils/helpers';
import { convertTextToId } from '@srcDS/utils/convertTextToId';
import { useLogo } from '@srcDS/hooks/useLogo';
import styles from './MobileMenu.module.scss';

import { MobileMenuBackButton, MobileMenuArrowRight, CloseCross } from '../../../atoms/icons';
import { useMenuContext } from '@srcDS/provider/menu/MenuContext';
import MenuLink from '@srcDS/components/molecules/headerMenu/menuLink/MenuLink';
import { useNavigation } from './useNavigation';
import { getTileToRenderOnMobile, staticMenuItems } from '@srcDS/components/molecules/headerMenu/mobileMenu/utils';
import { NavigationBanner } from '@srcDS/components/organisms/header/navigation/NavigationBanner/NavigationBanner';
import {
  getContentfulNavigationTiles,
  mapContentfulToNavigationTile,
} from '@srcDS/components/organisms/header/navigation/NavigationBanner/helpers';

const MobileMenu = ({
  setShownMenu = () => {},
  categories = [],
  shownMenu = false,
  storeLocatorLink,
  locales,
  contentfulNavigationTiles,
}) => {
  const [activeMenuLevelShown, setActiveMenuLevelShown] = useState(null);
  const { logoUrl, title } = useLogo();
  const { mobileMenuLevels, setMobileMenuLevels } = useMenuContext();

  useScrollLock(shownMenu);

  const closeMenu = () => {
    try {
      window?.zE && window?.zE('messenger:set', 'zIndex', 999999);
    } catch (e) {
      console.error(e);
    }
    setShownMenu(false);
    setMobileMenuLevels([]);
    setActiveMenuLevelShown(null);
  };

  const goBack = () => {
    const updatedLevels = mobileMenuLevels.slice(0, -1);
    const previousLevel = updatedLevels[updatedLevels.length - 1];

    setActiveMenuLevelShown(previousLevel);
    setMobileMenuLevels(updatedLevels);
  };

  return (
    <>
      <span className={styles.overlay} onClick={closeMenu} />
      <div
        className={clsx(styles.mobileMenu, {
          [styles.active]: shownMenu,
        })}
      >
        <div className={styles.mobileMenuHeader}>
          {Boolean(mobileMenuLevels.length) ? (
            <div className={styles.mobileMenuBackButton} onClick={goBack}>
              <MobileMenuBackButton />
              <span className={styles.mobileMenuBackButtonText}>{activeMenuLevelShown ?? ''}</span>
            </div>
          ) : (
            logoUrl && (
              <img
                width="100%"
                height="100%"
                src={logoUrl}
                alt={title ?? 'Logo'}
                className={styles.logoImage}
                onClick={() => {
                  closeMenu();
                  app.getRouter().history.replace('/');
                }}
              />
            )
          )}
          <Button variant="primary" as="text" onClick={closeMenu} ariaLabel="Close">
            <CloseCross width="28" height="28" />
          </Button>
        </div>

        {categories.map((category, index) => (
          <Fragment key={`id_${category.name}_${index}`}>
            <Category
              category={category}
              setShownMenu={closeMenu}
              insertAfter={<NavigationBanner category={category} setShownMenu={closeMenu} />}
              activeMenuLevelShown={activeMenuLevelShown}
              setActiveMenuLevelShown={setActiveMenuLevelShown}
              contentfulNavigationTiles={contentfulNavigationTiles}
            />
            {category.tree && categories.length === index + 1 && (
              <div className={styles.mobileMenuMainFooter}>
                <div>
                  {staticMenuItems.map((item, index) => {
                    if (item.type === 'externalLink') {
                      return (
                        <Link
                          key={`extraLink_${index}_${item.name}`}
                          className={styles.mobileMenuMainMenu}
                          link={{ reference: storeLocatorLink }}
                          onClick={closeMenu}
                          ariaLabel={item.name}
                          data-menu-option={convertTextToId(item.name)}
                        >
                          {item.name}
                        </Link>
                      );
                    }
                    return (
                      <span
                        key={`default_${index}_${item.name}`}
                        className={styles.mobileMenuMainMenu}
                        data-menu-option={convertTextToId(item.name)}
                        onClick={() => {
                          closeMenu();
                          if (item.type === 'dynamicPage') {
                            return app.getRouter().push(item.route);
                          }
                          app.getRouter().history.replace(item.route);
                        }}
                      >
                        {item.name}
                      </span>
                    );
                  })}
                </div>
                <CountrySelector items={locales} type="fullsize" />
              </div>
            )}
          </Fragment>
        ))}
      </div>
    </>
  );
};

const Category = ({
  category,
  setShownMenu,
  activeMenuLevelShown,
  setActiveMenuLevelShown,
  contentfulNavigationTiles,
}) => {
  const { getMenuItemData, mobileMenuLevels, setMobileMenuLevels } = useMenuContext();

  const { currentLevelName, showNestedCategory } = useNavigation({
    mobileMenuLevels,
    setMobileMenuLevels,
  });

  const { children, isVisibleInCurrentLocale, navItemColor, isVisibleInMenu, hasChildren } = getMenuItemData(category);

  if (!isVisibleInCurrentLocale || !isVisibleInMenu) {
    return null;
  }

  const linkColor = getHexColor(navItemColor) ?? '';

  const contentfulTiles = getContentfulNavigationTiles(contentfulNavigationTiles, category?._url) ?? [];
  const frontasticTiles =
    category?.configuration?.navigationTiles || category?.tree?.configuration?.navigationTiles || [];
  const mappedContentfulTiles = contentfulTiles.map(mapContentfulToNavigationTile);

  const navigationTiles = [...frontasticTiles, ...mappedContentfulTiles];
  const tileToRender = getTileToRenderOnMobile(navigationTiles);
  const nestedChildren = (parentName) =>
    hasChildren &&
    children.map((child, index) => (
      <Fragment key={`category_${child.nodeId}_${index}`}>
        <Category
          category={child}
          setShownMenu={setShownMenu}
          parentCategory={parentName}
          activeMenuLevelShown={activeMenuLevelShown}
          setActiveMenuLevelShown={setActiveMenuLevelShown}
          contentfulNavigationTiles={contentfulNavigationTiles}
        />
        {index === children.length - 1 && tileToRender && (
          <NavigationBanner tile={tileToRender} setShownMenu={setShownMenu} />
        )}
      </Fragment>
    ));

  const handleClick = (e) => {
    if (hasChildren) {
      setActiveMenuLevelShown(category.name);
      showNestedCategory(e, category.name);
    } else {
      setShownMenu(false);
    }
  };

  return (
    <>
      <div
        className={styles.mobileMenuMainMenu}
        style={{ color: linkColor }}
        data-menu-option={convertTextToId(category.name)}
        onClick={handleClick}
      >
        {hasChildren ? (
          <>
            {category.name} <MobileMenuArrowRight />
          </>
        ) : (
          <MenuLink category={category} linkColor={linkColor} />
        )}
      </div>
      {hasChildren && (
        <div
          className={clsx(styles.nestedContainer, {
            [styles.nestedContainerShown]:
              currentLevelName === category.name && mobileMenuLevels.includes(category.name),
          })}
        >
          {nestedChildren(category.name)}
        </div>
      )}
    </>
  );
};

export default MobileMenu;
